import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, RotateCw, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY_RECIPE = { title: "", blurb: "", ingredients: [""], method: "" };
const EMPTY_PERK = { title: "", code: "", detail: "" };
const EMPTY_POINT = { n: "", how: "" };

function Field({ label, value, onChange, multiline, rows = 3, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full px-3 py-2 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
          style={{ borderColor: "var(--sano-border)" }}
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
          style={{ borderColor: "var(--sano-border)" }}
          data-testid={testid}
        />
      )}
    </label>
  );
}

function ArrayEditor({ label, values, onChange, placeholder = "New item", testid }) {
  const update = (i, v) => onChange(values.map((x, idx) => idx === i ? v : x));
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const add = () => onChange([...values, ""]);
  return (
    <div data-testid={testid}>
      <div className="eyebrow mb-2">{label}</div>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={v}
              placeholder={placeholder}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 px-3 py-2 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
              style={{ borderColor: "var(--sano-border)" }}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-2 border rounded hover:bg-[#F0EBE1]"
              style={{ borderColor: "var(--sano-border)" }}
              aria-label="Remove"
            >
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-2 inline-flex items-center gap-1.5 text-xs hover:underline"
        style={{ color: "var(--sano-olive)" }}
      >
        <Plus size={12} strokeWidth={1.5} /> Add
      </button>
    </div>
  );
}

export default function CircleEditor({ token }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/circle/content", { params: { token } });
      setContent(data);
    } catch {
      toast.error("Couldn't load Circle content.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!content) return;
    setSaving(true);
    try {
      const payload = {
        seasonTitle: content.seasonTitle || "",
        intro: content.intro || "",
        ritualOfTheMonth: {
          title: content.ritualOfTheMonth?.title || "",
          steps: (content.ritualOfTheMonth?.steps || []).filter(Boolean),
        },
        recipes: (content.recipes || []).map((r) => ({
          title: r.title || "",
          blurb: r.blurb || "",
          ingredients: (r.ingredients || []).filter(Boolean),
          method: r.method || "",
        })),
        member_perks: (content.member_perks || []).map((p) => ({
          title: p.title || "",
          code: p.code || "",
          detail: p.detail || "",
        })),
        acupressureProtocol: {
          title: content.acupressureProtocol?.title || "",
          points: (content.acupressureProtocol?.points || []).map((pt) => ({
            n: pt.n || "",
            how: pt.how || "",
          })),
        },
      };
      await api.put("/admin/circle/content", payload, { params: { token } });
      toast.success("Saved. Members will see the new content on next visit.");
    } catch {
      toast.error("Couldn't save. Check your token and try again.");
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    try {
      const { data } = await api.get("/admin/circle/password", { params: { token } });
      if (data?.password) {
        localStorage.setItem("sano_circle_pass", data.password);
      }
      window.open("/circle", "_blank", "noopener");
    } catch {
      // Open anyway — they may already have the password stored locally
      window.open("/circle", "_blank", "noopener");
    }
  };

  if (loading || !content) {
    return (
      <div className="p-10 border text-center text-sm" style={{ borderColor: "var(--sano-border)", color: "var(--sano-ink-2)" }}>
        Loading Circle content…
      </div>
    );
  }

  // Helpers to mutate nested state
  const set = (k) => (v) => setContent({ ...content, [k]: v });
  const setRitual = (k) => (v) => setContent({
    ...content, ritualOfTheMonth: { ...content.ritualOfTheMonth, [k]: v },
  });
  const setProtocol = (k) => (v) => setContent({
    ...content, acupressureProtocol: { ...content.acupressureProtocol, [k]: v },
  });
  const setRecipe = (i, k) => (v) => setContent({
    ...content,
    recipes: content.recipes.map((r, idx) => idx === i ? { ...r, [k]: v } : r),
  });
  const setPerk = (i, k) => (v) => setContent({
    ...content,
    member_perks: content.member_perks.map((p, idx) => idx === i ? { ...p, [k]: v } : p),
  });
  const setPoint = (i, k) => (v) => setContent({
    ...content,
    acupressureProtocol: {
      ...content.acupressureProtocol,
      points: content.acupressureProtocol.points.map((pt, idx) => idx === i ? { ...pt, [k]: v } : pt),
    },
  });

  return (
    <div className="space-y-10" data-testid="circle-editor">
      {/* Header / actions */}
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm text-[#F9F6F0] disabled:opacity-50"
          style={{ background: "var(--sano-olive)" }}
          data-testid="circle-editor-save"
        >
          <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          onClick={preview}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border rounded-full hover:bg-[#F0EBE1]"
          style={{ borderColor: "var(--sano-border)" }}
          data-testid="circle-editor-preview"
        >
          <ExternalLink size={14} strokeWidth={1.5} /> Preview as member
        </button>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border rounded-full hover:bg-[#F0EBE1]"
          style={{ borderColor: "var(--sano-border)" }}
        >
          <RotateCw size={14} strokeWidth={1.5} /> Reload
        </button>
        {content.updated_at && (
          <span className="text-xs" style={{ color: "var(--sano-ink-2)" }}>
            Last saved {new Date(content.updated_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* Headline section */}
      <section className="p-6 border space-y-4" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}>
        <Field label="Season title" value={content.seasonTitle} onChange={set("seasonTitle")} testid="ce-season-title" />
        <Field label="Intro paragraph" value={content.intro} onChange={set("intro")} multiline rows={4} testid="ce-intro" />
      </section>

      {/* Ritual */}
      <section className="p-6 border space-y-4" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}>
        <div className="eyebrow">Ritual of the month</div>
        <Field label="Ritual title" value={content.ritualOfTheMonth?.title} onChange={setRitual("title")} testid="ce-ritual-title" />
        <ArrayEditor
          label="Steps (in order)"
          values={content.ritualOfTheMonth?.steps || []}
          onChange={setRitual("steps")}
          placeholder="One step at a time"
          testid="ce-ritual-steps"
        />
      </section>

      {/* Recipes */}
      <section className="p-6 border space-y-6" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}>
        <div className="flex items-center justify-between">
          <div className="eyebrow">Recipes for this season</div>
          <button
            onClick={() => setContent({ ...content, recipes: [...(content.recipes || []), { ...EMPTY_RECIPE }] })}
            className="inline-flex items-center gap-1.5 text-xs hover:underline"
            style={{ color: "var(--sano-olive)" }}
          >
            <Plus size={12} strokeWidth={1.5} /> Add recipe
          </button>
        </div>
        {(content.recipes || []).map((r, i) => (
          <div key={i} className="border p-5 space-y-3" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg-2)" }}>
            <div className="flex items-center justify-between">
              <div className="eyebrow">Recipe {i + 1}</div>
              <button
                onClick={() => setContent({ ...content, recipes: content.recipes.filter((_, idx) => idx !== i) })}
                className="p-1.5 border rounded hover:bg-[#F0EBE1]"
                style={{ borderColor: "var(--sano-border)" }}
                aria-label="Remove recipe"
              >
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            </div>
            <Field label="Title" value={r.title} onChange={setRecipe(i, "title")} />
            <Field label="Blurb (one line)" value={r.blurb} onChange={setRecipe(i, "blurb")} />
            <ArrayEditor
              label="Ingredients"
              values={r.ingredients || []}
              onChange={(v) => setRecipe(i, "ingredients")(v)}
              placeholder="e.g. 1 ripe pear, diced"
            />
            <Field label="Method" value={r.method} onChange={setRecipe(i, "method")} multiline rows={4} />
          </div>
        ))}
      </section>

      {/* Member perks */}
      <section className="p-6 border space-y-6" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}>
        <div className="flex items-center justify-between">
          <div className="eyebrow">Member perks</div>
          <button
            onClick={() => setContent({ ...content, member_perks: [...(content.member_perks || []), { ...EMPTY_PERK }] })}
            className="inline-flex items-center gap-1.5 text-xs hover:underline"
            style={{ color: "var(--sano-olive)" }}
          >
            <Plus size={12} strokeWidth={1.5} /> Add perk
          </button>
        </div>
        {(content.member_perks || []).map((p, i) => (
          <div key={i} className="border p-5 grid sm:grid-cols-12 gap-3" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg-2)" }}>
            <div className="sm:col-span-5"><Field label="Title" value={p.title} onChange={setPerk(i, "title")} /></div>
            <div className="sm:col-span-2"><Field label="Code" value={p.code} onChange={setPerk(i, "code")} /></div>
            <div className="sm:col-span-4"><Field label="Detail" value={p.detail} onChange={setPerk(i, "detail")} multiline rows={2} /></div>
            <div className="sm:col-span-1 flex items-end">
              <button
                onClick={() => setContent({ ...content, member_perks: content.member_perks.filter((_, idx) => idx !== i) })}
                className="p-2 border rounded hover:bg-[#F0EBE1] w-full flex justify-center"
                style={{ borderColor: "var(--sano-border)" }}
                aria-label="Remove perk"
              >
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </section>

      {/* Acupressure protocol */}
      <section className="p-6 border space-y-4" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}>
        <div className="eyebrow">Members' acupressure protocol</div>
        <Field label="Protocol title" value={content.acupressureProtocol?.title} onChange={setProtocol("title")} />
        <div className="flex items-center justify-between mt-4">
          <div className="eyebrow">Points</div>
          <button
            onClick={() => setProtocol("points")([...(content.acupressureProtocol?.points || []), { ...EMPTY_POINT }])}
            className="inline-flex items-center gap-1.5 text-xs hover:underline"
            style={{ color: "var(--sano-olive)" }}
          >
            <Plus size={12} strokeWidth={1.5} /> Add point
          </button>
        </div>
        {(content.acupressureProtocol?.points || []).map((pt, i) => (
          <div key={i} className="border p-4 grid sm:grid-cols-12 gap-3 mt-2" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg-2)" }}>
            <div className="sm:col-span-4"><Field label="Point" value={pt.n} onChange={setPoint(i, "n")} /></div>
            <div className="sm:col-span-7"><Field label="How to use" value={pt.how} onChange={setPoint(i, "how")} multiline rows={2} /></div>
            <div className="sm:col-span-1 flex items-end">
              <button
                onClick={() => setProtocol("points")(content.acupressureProtocol.points.filter((_, idx) => idx !== i))}
                className="p-2 border rounded hover:bg-[#F0EBE1] w-full flex justify-center"
                style={{ borderColor: "var(--sano-border)" }}
                aria-label="Remove point"
              >
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </section>

      <div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm text-[#F9F6F0] disabled:opacity-50"
          style={{ background: "var(--sano-olive)" }}
          data-testid="circle-editor-save-bottom"
        >
          <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
